/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import {
  MutationKey,
  useMutation,
  UseMutationResult,
} from "@tanstack/react-query";
import { GramCore } from "../core.js";
import { triggersCreate } from "../funcs/triggersCreate.js";
import { combineSignals } from "../lib/primitives.js";
import { RequestOptions } from "../lib/sdks.js";
import { TriggerInstance } from "../models/components/triggerinstance.js";
import { GramError } from "../models/errors/gramerror.js";
import {
  ConnectionError,
  InvalidRequestError,
  RequestAbortedError,
  RequestTimeoutError,
  UnexpectedClientError,
} from "../models/errors/httpclienterrors.js";
import { ResponseValidationError } from "../models/errors/responsevalidationerror.js";
import { SDKValidationError } from "../models/errors/sdkvalidationerror.js";
import { ServiceError } from "../models/errors/serviceerror.js";
import {
  CreateTriggerInstanceRequest,
  CreateTriggerInstanceSecurity,
} from "../models/operations/createtriggerinstance.js";
import { unwrapAsync } from "../types/fp.js";
import { useGramContext } from "./_context.js";
import { MutationHookOptions } from "./_types.js";

export type CreateTriggerMutationVariables = {
  request: CreateTriggerInstanceRequest;
  security?: CreateTriggerInstanceSecurity | undefined;
  options?: RequestOptions;
};

export type CreateTriggerMutationData = TriggerInstance;

export type CreateTriggerMutationError =
  | ServiceError
  | GramError
  | ResponseValidationError
  | ConnectionError
  | RequestAbortedError
  | RequestTimeoutError
  | InvalidRequestError
  | UnexpectedClientError
  | SDKValidationError;

/**
 * createTriggerInstance triggers
 *
 * @remarks
 * Create a trigger instance.
 */
export function useCreateTriggerMutation(
  options?: MutationHookOptions<
    CreateTriggerMutationData,
    CreateTriggerMutationError,
    CreateTriggerMutationVariables
  >,
): UseMutationResult<
  CreateTriggerMutationData,
  CreateTriggerMutationError,
  CreateTriggerMutationVariables
> {
  const client = useGramContext();
  return useMutation({
    ...buildCreateTriggerMutation(client, options),
    ...options,
  });
}

export function mutationKeyCreateTrigger(): MutationKey {
  return ["@gram/client", "triggers", "create"];
}

export function buildCreateTriggerMutation(
  client$: GramCore,
  hookOptions?: RequestOptions,
): {
  mutationKey: MutationKey;
  mutationFn: (
    variables: CreateTriggerMutationVariables,
  ) => Promise<CreateTriggerMutationData>;
} {
  return {
    mutationKey: mutationKeyCreateTrigger(),
    mutationFn: function createTriggerMutationFn({
      request,
      security,
      options,
    }): Promise<CreateTriggerMutationData> {
      const mergedOptions = {
        ...hookOptions,
        ...options,
        fetchOptions: {
          ...hookOptions?.fetchOptions,
          ...options?.fetchOptions,
          signal: combineSignals(
            hookOptions?.fetchOptions?.signal,
            options?.fetchOptions?.signal,
          ),
        },
      };
      return unwrapAsync(triggersCreate(
        client$,
        request,
        security,
        mergedOptions,
      ));
    },
  };
}
